<script setup>
  import { ref, onMounted, toRaw } from 'vue'
  import CatalogPublicationFilters from '../pages/CatalogPublicationFilters.vue';
  import PublicationTable from './catalog/PublicationTable.vue';
  import { useCatalogPublicationSettingStore } from '../stores/CatalogPublicationSettingStore.js';
  import {
    mapSettings,
    clearFiltersAndColumnsCache
  } from './helpers/CatalogPublicationFiltersHelper';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';
  import { useTableColumnStore } from '../stores/TableColumnStore.js';
  import { useUsersStore } from '../stores/UsersStore.js';

  const catalogPublicationSettingStore = useCatalogPublicationSettingStore();
  const usersStore = useUsersStore();
  const scope = 'components.catalog_publication.'
  const title = ref('Catalog Publication');
  const lang = ref();
  const router = useRouter();
  const settings = ref([]);
  const selectedSetting = ref(null);
  const selectedSettingId = ref(0);
  const currentUser = ref(null);
  const settingsLoaded = ref(false);
  const showFilters = ref(false);
  const publicationFiltersRef = ref(null);
  const params = new URLSearchParams(window.location.search);
  const returnUrl = '/frontend/catalog/publications?setting_id=';
  const { t } = useI18n();

  const {
    setOrder,
    getOriginals
  } = useTableColumnStore();
  document.title = title.value;
  lang.value = localStorage.getItem("lang");

  onMounted( async () => {
    selectedSettingId.value = params.get('setting_id') ? parseInt(params.get('setting_id')) : 0;
    clearFiltersAndColumnsCache();

    await obtainUser();
    await obtainSettings();
  });

  const obtainUser = async () => {
    currentUser.value = await usersStore.fetchUser();
  };

  const obtainSettings = async () => {
    const settingsValues = await catalogPublicationSettingStore.fetchSettings();
    settings.value = mapSettings(toRaw(settingsValues.data), currentUser.value.data.id);
    settingsLoaded.value = true;
    const setting = settings.value.find(setting => setting.id === selectedSettingId.value);
    selectedSetting.value = setting;
    showFilters.value = true;
  };

  const modifySetting = async () => {
    publicationFiltersRef.value.openModifyModal(selectedSetting.value);
  }

  const resetSettingsAndRedirect = async () => {
    selectedSettingId.value = 0;
    selectedSetting.value = null;
    await setOrder(getOriginals('filters'), 'filters');
    await setOrder(getOriginals('columns'), 'columns');
    router.push(`${returnUrl}0&set_values=false`);
  };

  const deleteSetting = async () => {
    if(confirm(t(`${scope}save_settings.are_you_sure`))) {
      await catalogPublicationSettingStore.destroyPublicationSetting(selectedSettingId.value);
      resetSettingsAndRedirect();
    }
  };

  const allSettings = () => {
    window.location.href = '/frontend/catalog/publications/settings?page=1';
  };

  const redirectWithSettings = async (e) => {
    showFilters.value = false;
    const settingId = parseInt(e.target.value);
    if (settingId == 0) {
      resetSettingsAndRedirect();
      return;
    }
    const setting = settings.value.find(setting => setting.id === settingId);
    selectedSetting.value = setting;
    selectedSettingId.value = settingId;
    const query = selectedSetting.value.filtersUrlQuery;
    await setOrder(toRaw(selectedSetting.value.filterPositions), 'filters');
    await setOrder(toRaw(selectedSetting.value.columnPositions), 'columns');
    router.push(`${returnUrl}${settingId}&${query}&set_values=true&selected_setting_name=${selectedSetting.value.name}`);
  };
</script>

<template>
  <div class="container-fluid">
    <div id="teleport-alert"></div>
    <div class="row align-items-center mt-3">
      <div class="col-4">&nbsp;</div>
      <div class="col-4 text-center">
        <h1>{{t(`${scope}title`)}}</h1>
      </div>
      <div class="col-4">
        <div class="row align-items-center">
          <div class="col-12 my-4">
            <div class="d-flex align-items-center gap-2">
              <span class="text-nowrap">{{t(`${scope}saved_settings`)}}:</span>
              <select
                class="form-select"
                @input="redirectWithSettings($event)"
                v-model="selectedSettingId"
              >
                <option value="0">- {{t(`${scope}filters.none`)}} -</option>
                <option v-for="(setting) in settings" :value="setting.id">
                  {{ setting.name }}
                </option>
              </select>
            </div>

            <small class="float-start my-2">
              <span role="button" class="link-primary" @click="allSettings">
                {{t(`${scope}save_settings.access_all_saved_settings`)}}
              </span>
            </small>

            <div v-if="selectedSettingId !== 0">
              <small class="float-end my-2">
                <span role="button" class="link-primary" @click="modifySetting">
                  {{t(`${scope}save_settings.modify`)}}
                </span>
                &nbsp; &nbsp;
                <span role="button" class="link-primary" @click="deleteSetting">
                  {{t(`${scope}save_settings.delete`)}}
                </span>
              </small>
            </div>
          </div>
        </div>
      </div>
      <div class="col-4" v-if="!settingsLoaded">
        &nbsp;
      </div>
    </div>

    <CatalogPublicationFilters
      :lang="lang"
      v-if="showFilters && settingsLoaded"
      :urlQuery="selectedSetting ? selectedSetting.filtersUrlQuery : null"
      ref="publicationFiltersRef"
    />
    <hr class="hr" />
    <PublicationTable
      ref="publicationTable"
      :can-flag-unsafe="currentUser?.data?.attributes?.can_flag_unsafe === true"
      :can-manage-selection="currentUser?.data?.attributes?.admin_or_support === true"
    />
  </div>
</template>

<style scoped lang="scss">
  :deep(.custom-offcanvas) {
    min-height: 540px !important;
  }
  datalist {
    position: absolute;
    margin-top: 2.6em;
    max-height: 20em;
    width: auto !important;
    overflow-x: hidden;
    overflow-y: auto;
    z-index: 1000;
    border: 1px solid #e8e8e8 !important;
  }
</style>
